import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';

import { Button as ButtonV2, Heading } from '@restworld/ui-ds-base';
import { cn, formatDateToDayMonthYear } from '@restworld/utils-common';
import { Invoice } from '@restworld/utility-types';
import { SkeletonEmployerCardListLoading } from '@restworld/ui-components';
import { EmployerInvoiceCard } from '../legacy';

import { useDownloadInvoiceMutation, useFetchInvoicesQuery, usePayInvoiceMutation } from './employer-invoices.hooks';

// Invoices whose quantity counts days of a job position online (metered PPU and the
// prepaid credits), not subscription slots.
const PPU_DAY_PAYMENT_TYPES: Invoice['payment_type'][] = ['ppu_subscription', 'credit_package', 'credit_subscription'];

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IEmployerInvoicesProps {}
export const EmployerInvoices: React.FunctionComponent<IEmployerInvoicesProps> = () => {
  const { invoicesList, query, hasNextPage } = useFetchInvoicesQuery();
  const { mutate: downloadInvoice, isPending: downloadInvoicePending, variables } = useDownloadInvoiceMutation();
  const { mutate: payInvoice, isPending: payInvoicePending } = usePayInvoiceMutation();
  const { t } = useTranslation('payment-and-invoices');

  const getInvoiceStatusInfo = React.useCallback(
    (invoice: Invoice) => {
      if (invoice['paid?']) {
        return {
          status: 'paid' as const,
          statusText: invoice.payment_date
            ? t('paid_on', {
                paymentDate: formatDateToDayMonthYear(invoice.payment_date),
              })
            : t('paid'),
        };
      }
      if (invoice.number_of_installments_paid_for_slots) {
        return {
          status: 'installment' as const,
          statusText: `${invoice.number_of_installments_paid_for_slots}/3`,
        };
      }
      return {
        status: 'to_be_paid' as const,
        statusText: t('to_be_paid'),
      };
    },
    [t]
  );

  const groupedInvoicesList = React.useMemo(
    () =>
      Object.entries(
        invoicesList.reduce((acc, invoice) => {
          const year = new Date(invoice.issue_date).getFullYear();
          if (!acc[year]) acc[year] = [];
          acc[year].push(invoice);
          return acc;
        }, {} as Record<number, Invoice[]>)
      ).sort(([yearA], [yearB]) => Number(yearB) - Number(yearA)),
    [invoicesList]
  );

  const renderInvoicesList = React.useMemo(() => {
    return groupedInvoicesList.map(([year, invoices]) => (
      <li key={year} className="flex flex-col gap-4">
        <Heading size={5} color="neutral">
          {year}
        </Heading>
        <ul className="flex flex-col gap-4">
          {invoices.map((invoice) => {
            const {
              id,
              company,
              billing_company_name,
              issue_date,
              quantity,
              discount,
              amount,
              payment_type,
              invoice_number,
              hosted_invoice_url,
            } = invoice;

            const handlePayInvoice = () => {
              if (payInvoicePending) return;
              payInvoice({ id });
            };

            const { status, statusText } = getInvoiceStatusInfo(invoice);

            return (
              <li key={id}>
                <EmployerInvoiceCard
                  invoiceNumber={invoice_number}
                  companyName={billing_company_name || company?.name || ''}
                  issueDate={formatDateToDayMonthYear(issue_date)}
                  slots={quantity}
                  discount={discount}
                  amount={amount}
                  status={status}
                  statusText={statusText}
                  onDownloadInvoice={() => downloadInvoice({ id, invoiceNumber: invoice_number })}
                  isAnnualSubscription={payment_type === 'annual_subscription'}
                  isPpu={PPU_DAY_PAYMENT_TYPES.includes(payment_type)}
                  downloadInvoicePending={variables?.id === id ? downloadInvoicePending : null}
                  hostedInvoiceUrl={hosted_invoice_url}
                  onPayInvoice={handlePayInvoice}
                />
              </li>
            );
          })}
        </ul>
      </li>
    ));
  }, [
    groupedInvoicesList,
    getInvoiceStatusInfo,
    variables?.id,
    downloadInvoicePending,
    downloadInvoice,
    payInvoice,
    payInvoicePending,
  ]);

  return (
    <div className="flex-col justify-start gap-4 inline-flex w-full">
      <EmployerInvoicesHeader />
      {query?.isLoading ? (
        <SkeletonEmployerCardListLoading />
      ) : (
        <ul className="flex flex-col gap-[64px]">
          {renderInvoicesList}
          {hasNextPage && (
            <div className="flex justify-center pt-16">
              <ButtonV2
                StartIcon={PlusIcon}
                variant="tertiary"
                onClick={() => query.fetchNextPage()}
                loading={query.isFetchingNextPage}
              >
                {t('load_more_items')}
              </ButtonV2>
            </div>
          )}
        </ul>
      )}
    </div>
  );
};

export default EmployerInvoices;
const EmployerInvoicesHeader = () => {
  const { t } = useTranslation('payment-and-invoices');

  return (
    <HeaderWrapper>
      <TitleWrapper>
        <Heading color="neutral">{t('invoices_title')}</Heading>
      </TitleWrapper>
    </HeaderWrapper>
  );
};
const HeaderWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('self-stretch h-10 justify-between items-center inline-flex', className)} {...props} />
);
const TitleWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('justify-start items-center gap-2 flex', className)} {...props} />
);
